import Request from '@/services/request';
import {
  UMBRELLA_CAMPAIGNS_LIST,
  UMBRELLA_CAMPAIGNS_DETAIL,
  UMBRELLA_CAMPAIGNS_LEAVE,
  UMBRELLA_CAMPAIGNS_JOIN,
  UMBRELLA_CAMPAIGNS_PRODUCTS,
  UMBRELLA_CAMPAIGNS_PRODUCTS_ACTIVE_FILTERS,
  UMBRELLA_CAMPAIGNS_NOTICE_STATUS,
  SELLER_INFO,
  SELLEROFFICE_API_BASE_URL,
} from '@/utils/enum/endpoints';
import store from '@/store';
import { ToastMessageTypes } from '@/utils/enum/toastMessageTypes';
import {
  STATUS_BADGE,
  STATUS,
  RETURN_STATUS,
} from '@/utils/enum/umbrellaCampaign';

const request = new Request();

const extractFailedProducts = (responseData) => {
  return (
    responseData?.failedProducts ||
    responseData?.data?.failedProducts ||
    responseData?.body?.failedProducts ||
    []
  );
};

const toNumericPriceForCompare = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const normalized = String(value).replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatDateString = (dateString) => {
  if (!dateString) return '';

  return dateString.replace(' ', ', ');
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year}, ${hours}:${minutes}`;
};

const getStatusClass = (status, statusBadge) => {
  // API'den gelen statusBadge'e göre class belirle
  if (statusBadge === STATUS_BADGE.ONGOING) {
    return RETURN_STATUS.ONGOING;
  }

  if (
    statusBadge === STATUS_BADGE.ENDING_SOON ||
    statusBadge === STATUS_BADGE.ENDING
  ) {
    return RETURN_STATUS.ENDING;
  }

  if (
    statusBadge === STATUS_BADGE.UPCOMING ||
    statusBadge === STATUS_BADGE.UPCOMING_SOON
  ) {
    return RETURN_STATUS.UPCOMING;
  }

  // Fallback: status'a göre
  if (status === STATUS.ONGOING) {
    return RETURN_STATUS.ONGOING;
  }

  if (status === STATUS.UPCOMING) {
    return RETURN_STATUS.UPCOMING;
  }

  if (status === STATUS.ENDING_SOON || status === STATUS.ENDING) {
    return RETURN_STATUS.ENDING;
  }

  return RETURN_STATUS.ONGOING;
};

const mapCampaignData = (apiData) => {
  return apiData.map((campaign) => {
    // startDate ve endDate kontrolü
    const startDate = campaign.startDate
      ? formatDateString(campaign.startDate)
      : '';
    const endDate = campaign.endDate ? formatDateString(campaign.endDate) : '';
    const dateRange =
      startDate && endDate
        ? `${startDate} - ${endDate}`
        : startDate || endDate || '';

    return {
      id: campaign.campaignId,
      name: campaign.name || '',
      commissionDiscount:
        campaign.commissionDiscount &&
        Number(campaign.commissionDiscount) !== 0
          ? `%${campaign.commissionDiscount} Komisyon İndirimi`
          : '',
      status: campaign.statusBadge || campaign.status || '',
      statusClass: getStatusClass(campaign.status, campaign.statusBadge),
      dateRange,
      endDate,
    };
  });
};

export const getCampaigns = async (params) => {
  try {
    // Tab'a göre joinedOnly parametresini belirle
    const tab = params.tab || 'available';
    const { tab: _, ...queryParams } = params;

    if (tab === 'joined') {
      queryParams.joinedOnly = true;
    }

    const response = await request.get(UMBRELLA_CAMPAIGNS_LIST, queryParams, {
      baseURL: SELLEROFFICE_API_BASE_URL,
    });

    const responseData = response?.data ?? response;

    if (responseData) {
      const {
        campaigns: campaignsData,
        joinedCount,
        joinableCount,
      } = responseData || {};
      const rawCampaigns = campaignsData?.content || [];
      const campaigns =
        mapCampaignData(Array.isArray(rawCampaigns) ? rawCampaigns : []) || [];
      const totalPages = campaignsData?.totalPages || 0;
      const totalElements = campaignsData?.totalElements || 0;
      const sortType = responseData?.filter?.sortType || params.sortType || '';

      return {
        campaigns,
        totalPages,
        totalElements,
        sortType,
        joinedCount,
        joinableCount,
      };
    }

    return {
      campaigns: [],
      totalPages: 0,
      totalElements: 0,
      sortType: '',
      joinedCount: 0,
      joinableCount: 0,
    };
  } catch (error) {
    return {
      campaigns: [],
      totalPages: 0,
      totalElements: 0,
      sortType: '',
      joinedCount: 0,
      joinableCount: 0,
      error,
    };
  }
};

export const getCampaignDetail = async (campaignId) => {
  try {
    const endpoint = UMBRELLA_CAMPAIGNS_DETAIL.replace(
      '{campaignId}',
      campaignId,
    );
    const response = await request.get(endpoint, null, {
      baseURL: SELLEROFFICE_API_BASE_URL,
    });

    if (response && response.data) {
      const { data } = response;

      return {
        name: data.campaignName || '',
        dateRange:
          data.startDate && data.endDate
            ? `${formatTimestamp(data.startDate)} - ${formatTimestamp(
                data.endDate,
              )}`
            : '',
        eligibleProductCount: data.suitableProductCount || 0,
        participatingSellerCount: data.totalCampaignApplyCount || 0,
        last24HoursSales: data.orderCountLast24Hours || 0,
        campaignHtml: data.campaignHtml || '',
        sellerShare: data.sellerShare || 0,
        n11Share: data.n11Share || 0,
        commissionDiscount: data.commissionDiscount ?? null,
        canBeUsedWithVoucher: data.canBeUsedWithVoucher || false,
        quantityBasedCascading: data.quantityBasedCascading || false,
        orderCountLast24Hours: data.orderCountLast24Hours || 0,
        sellerOrderCount: data.sellerStats?.sellerOrderCount || 0,
        sellerTotalGMV: data.sellerStats?.sellerTotalGMV || 0,
      };
    }

    return {
      name: '',
      dateRange: '',
      eligibleProductCount: 0,
      participatingSellerCount: 0,
      last24HoursSales: 0,
      campaignHtml: '',
      sellerShare: 0,
      n11Share: 0,
      canBeUsedWithVoucher: false,
      quantityBasedCascading: false,
      orderCountLast24Hours: 0,
      sellerOrderCount: 0,
      sellerTotalGMV: 0,
    };
  } catch (error) {
    return {
      name: '',
      dateRange: '',
      eligibleProductCount: 0,
      participatingSellerCount: 0,
      last24HoursSales: 0,
      campaignHtml: '',
      sellerShare: 0,
      n11Share: 0,
      canBeUsedWithVoucher: false,
      quantityBasedCascading: false,
      orderCountLast24Hours: 0,
      sellerOrderCount: 0,
      sellerTotalGMV: 0,
      error,
    };
  }
};

export const getSellerInfo = async (campaignId) => {
  try {
    const params = campaignId ? { campaignId } : null;
    const response = await request.get(SELLER_INFO, params, {
      baseURL: SELLEROFFICE_API_BASE_URL,
    });

    const responseData = response?.data ?? response;

    if (responseData) {
      const categories = responseData?.categories || [];

      const brands = (responseData.brands || []).map((brandName) => ({
        id: brandName,
        name: brandName,
      }));

      return {
        categories,
        brands,
      };
    }

    return {
      categories: [],
      brands: [],
    };
  } catch (error) {
    return {
      categories: [],
      brands: [],
      error,
    };
  }
};

export const leaveCampaign = async (campaignId) => {
  try {
    const endpoint = UMBRELLA_CAMPAIGNS_LEAVE.replace(
      '{campaignId}',
      campaignId,
    );
    await request.post(endpoint, null, {
      baseURL: SELLEROFFICE_API_BASE_URL,
    });

    return { success: true };
  } catch (error) {
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description: 'Kampanyadan ayrılırken bir hata oluştu.',
    });

    return { success: false };
  }
};

export const joinCampaign = async (campaignId, productPrices) => {
  try {
    const endpoint = UMBRELLA_CAMPAIGNS_JOIN.replace(
      '{campaignId}',
      campaignId,
    );
    const fullUrl = `${SELLEROFFICE_API_BASE_URL}${endpoint}`;

    const requestBody = {
      campaignId: Number(campaignId),
      productPrices: productPrices.map((item) => ({
        productId: item.productId,
        minPrice: item.minPrice || 0,
        maxPrice: item.maxPrice,
      })),
    };

    const response = await request.post(fullUrl, requestBody, {
      baseURL: '',
    });

    const responseData = response?.data || response;
    const failedProducts = extractFailedProducts(responseData);

    if (response?.status === 200 || responseData?.status === 200) {
      if (failedProducts.length > 0) {
        store.commit('common/SET_TOASTER_DATA', {
          type: ToastMessageTypes.WARNING,
          description: 'Bazı ürünler kampanyaya eklenemedi.',
        });
      } else {
        store.commit('common/SET_TOASTER_DATA', {
          type: ToastMessageTypes.SUCCESS,
          description: 'Kampanyaya katıldınız',
        });
      }
      return {
        success: true,
        failedProducts,
      };
    }

    const errorMessage =
      responseData?.message ||
      response?.message ||
      response?.error?.data?.errors?.[0]?.message ||
      'Kampanyaya katılırken bir hata oluştu';
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description: errorMessage,
    });
    return {
      success: false,
      failedProducts,
      error: errorMessage,
    };
  } catch (error) {
    const errorData = error?.response?.data || error?.data || {};
    const errorMessage =
      errorData?.message ||
      error?.message ||
      errorData?.errors?.[0]?.message ||
      'Kampanyaya katılırken bir hata oluştu';
    const failedProducts = extractFailedProducts(errorData);
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description: errorMessage,
    });
    return {
      success: false,
      failedProducts,
      error: errorMessage,
    };
  }
};

export const getCampaignProducts = async (campaignId, searchCriteria = {}) => {
  try {
    const endpoint = UMBRELLA_CAMPAIGNS_PRODUCTS.replace(
      '{campaignId}',
      campaignId,
    );
    const fullUrl = `${SELLEROFFICE_API_BASE_URL}${endpoint}`;

    // Query params oluştur - sadece tanımlı değerleri gönder (false değerleri de dahil)
    const params = Object.fromEntries(
      Object.entries(searchCriteria).filter(([, value]) => {
        if (typeof value === 'boolean') return true; // includedInCampaign için
        if (typeof value === 'number') return true; // page, size, categoryId için
        if (Array.isArray(value)) return value.length > 0;

        return value !== undefined && value !== null && value !== '';
      }),
    );

    const response = await request.get(fullUrl, params, {
      baseURL: '',
    });

    let responseData = null;
    if (response?.data) {
      responseData = response.data;
    } else if (response) {
      responseData = response;
    }

    if (responseData?.productItems) {
      const productItems = responseData.productItems.content || [];
      const totalElements = responseData.productItems.totalElements || 0;
      const totalPages = responseData.productItems.totalPages || 0;

      const mappedProducts = productItems.map((item) => {
        const currentForCompare =
          item.currentPrice !== undefined && item.currentPrice !== null
            ? item.currentPrice
            : item.displayPrice;
        const currentNumeric = toNumericPriceForCompare(currentForCompare);
        const maxNumeric = toNumericPriceForCompare(item.maxPrice);
        const n11InputEmpty = currentNumeric > maxNumeric;

        return {
          id: item.sellerStockCode || item.productId,
          productId: item.productId,
          name: item.productName || '',
          image: item.productImage || '',
          stockCode: item.sellerStockCode || '',
          commissionRate:
            item.commission?.toString() ||
            item.discountedCommissionRate?.toString() ||
            '0',
          currentPrice:
            item.currentPrice?.toString() || item.displayPrice?.toString() || '0',
          suggestedMaxPrice: item.maxPrice?.toString() || '0',
          maxPrice: item.maxPrice || 0,
          n11SalePrice: item.displayPrice?.toString() || '0',
          price: n11InputEmpty ? null : item.displayPrice || 0,
          stock: item.stock ?? 0,
          isDisabled: false,
          includedInAnotherCampaign: Boolean(item.includedInAnotherCampaign),
        };
      });

      return {
        products: mappedProducts,
        totalElements,
        totalPages,
        searchCriteria: responseData.searchCriteria || {},
      };
    }

    return {
      products: [],
      totalElements: 0,
      totalPages: 0,
      searchCriteria: {},
    };
  } catch (error) {
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description: 'Ürünler yüklenirken bir hata oluştu.',
    });

    return {
      products: [],
      totalElements: 0,
      totalPages: 0,
      searchCriteria: {},
      error,
    };
  }
};

export const getCampaignProductActiveFilters = async (
  campaignId,
  searchCriteria = {},
) => {
  try {
    const endpoint = UMBRELLA_CAMPAIGNS_PRODUCTS_ACTIVE_FILTERS.replace(
      '{campaignId}',
      campaignId,
    );
    const fullUrl = `${SELLEROFFICE_API_BASE_URL}${endpoint}`;

    const params = Object.fromEntries(
      Object.entries(searchCriteria).filter(([, value]) => {
        if (typeof value === 'boolean') return true;
        if (typeof value === 'number') return true;
        if (Array.isArray(value)) return value.length > 0;

        return value !== undefined && value !== null && value !== '';
      }),
    );

    const response = await request.get(fullUrl, params, { baseURL: '' });
    const responseData = response?.data ?? response;

    const categories = (responseData?.categories || []).map((category) => ({
      id: category.categoryId,
      name: category.categoryName,
    }));

    const brands = (responseData?.brands || []).map((brand) => ({
      id: brand.brandName,
      name: brand.brandName,
    }));

    return { categories, brands };
  } catch (error) {
    return { categories: [], brands: [], error };
  }
};

export const getPromotionalNoticeStatus = async () => {
  try {
    const response = await request.get(UMBRELLA_CAMPAIGNS_NOTICE_STATUS, null, {
      baseURL: SELLEROFFICE_API_BASE_URL,
    });

    return response?.data?.data;
  } catch (error) {
    return false;
  }
};

export const addProductsToCampaign = async (campaignId, products) => {
  try {
    const endpoint = UMBRELLA_CAMPAIGNS_PRODUCTS.replace(
      '{campaignId}',
      campaignId,
    );
    const fullUrl = `${SELLEROFFICE_API_BASE_URL}${endpoint}`;

    const requestBody = products.map((item) => ({
      productId: item.productId,
      minPrice: item.minPrice ?? null,
      maxPrice: item.maxPrice ?? null,
    }));

    const response = await request.post(fullUrl, requestBody, { baseURL: '' });

    const responseData = response?.data || response;
    const failedProducts = extractFailedProducts(responseData);
    const status = responseData?.header?.status ?? response?.status;

    if (status === 200) {
      if (failedProducts.length > 0) {
        store.commit('common/SET_TOASTER_DATA', {
          type: ToastMessageTypes.WARNING,
          description: 'Bazı ürünler kampanyaya eklenemedi.',
        });
      }
      return {
        success: true,
        failedProducts,
      };
    }

    const errorMessage =
      responseData?.header?.message ||
      'Ürünler kampanyaya eklenirken bir hata oluştu';
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description: errorMessage,
    });
    return {
      success: false,
      failedProducts,
      error: errorMessage,
    };
  } catch (error) {
    const errorData = error?.response?.data || error?.data || {};
    const errorMessage =
      errorData?.header?.message ||
      errorData?.message ||
      error?.message ||
      'Ürünler kampanyaya eklenirken bir hata oluştu';
    const failedProducts = extractFailedProducts(errorData);
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description: errorMessage,
    });
    return {
      success: false,
      failedProducts,
      error: errorMessage,
    };
  }
};

export const removeProductsFromCampaign = async (campaignId, productIds) => {
  try {
    const endpoint = UMBRELLA_CAMPAIGNS_PRODUCTS.replace(
      '{campaignId}',
      campaignId,
    );
    const fullUrl = `${SELLEROFFICE_API_BASE_URL}${endpoint}`;

    await request.delete(fullUrl, { productIds }, { baseURL: '' });

    return { success: true };
  } catch (error) {
    const errorMessage =
      error?.data?.message ||
      error?.message ||
      'Ürünler kampanyadan çıkarılırken bir hata oluştu';
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description: errorMessage,
    });
    return {
      success: false,
      error: errorMessage,
    };
  }
};

export const updateCampaignProductPrices = async (
  campaignId,
  productPrices,
) => {
  try {
    const endpoint = UMBRELLA_CAMPAIGNS_PRODUCTS.replace(
      '{campaignId}',
      campaignId,
    );
    const fullUrl = `${SELLEROFFICE_API_BASE_URL}${endpoint}`;

    const requestBody = {
      productPrices: productPrices.map((item) => ({
        productId: item.productId,
        minPrice: item.minPrice || 0,
        maxPrice: item.maxPrice,
      })),
    };

    const response = await request.post(fullUrl, requestBody, {
      baseURL: '',
    });

    if (response?.status === 200 || response?.data?.status === 200) {
      store.commit('common/SET_TOASTER_DATA', {
        type: ToastMessageTypes.SUCCESS,
        description: 'Ürün fiyatları başarıyla güncellendi',
      });
      return {
        success: true,
        data: response?.data || response,
      };
    }
    const errorMessage =
      response?.data?.message ||
      response?.message ||
      'Fiyat güncellenirken bir hata oluştu';
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description: errorMessage,
    });
    return {
      success: false,
      error: errorMessage,
    };
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.data?.message ||
      error?.message ||
      'Fiyat güncellenirken bir hata oluştu';
    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.ERROR,
      description: errorMessage,
    });
    return {
      success: false,
      error: errorMessage,
    };
  }
};
